import { updateProject } from "@/components/common/project-provider/update-project";
import { useSettingsContext } from "@/components/common/settings-context/settings-context";
import { LoadingOverlay } from "@/components/ui/loading-overlay";
import { useErrorHandlers } from "@/errors/components/error-handling-context";
import { useAppDispatch } from "@/store/store-hooks";
import { WaypointsColoringOptions } from "@/store/view-options/view-options-slice";
import {
  holoBuilderColorToSphereXGColor,
  SphereXGColors,
  sphereXGToHolobuilderColors,
} from "@/utils/waypoints-colors-mapping";
import {
  ColorPalette,
  FaroIconButton,
  FaroText,
  FaroTooltip,
  TooltipLight,
  WarningIcon,
  yellow,
} from "@faro-lotv/flat-ui";
import { assert, validateEnumValue } from "@faro-lotv/foundation";
import {
  HoloBuilderColors,
  IElementRoomSection,
} from "@faro-lotv/ielement-types";
import {
  createMutationSetWaypointColor,
  useApiClientContext,
} from "@faro-lotv/service-wires";
import { Stack, SxProps } from "@mui/system";
import { useCallback, useMemo, useState, type JSX } from "react";

type EditWaypointColorProps = {
  /** The waypoint reference element holding the color information */
  waypointReference: IElementRoomSection;

  /** Callback after the color has been changed successfully. Use e.g. for tracking analytics. */
  onColorChanged?(): void;

  /** Whether to use dark styles */
  dark?: boolean;
};

const COLOR_OPTIONS = Object.values(SphereXGColors);

/**
 * @returns An input field to edit the name of a waypoint
 */
export function EditWaypointColor({
  waypointReference,
  onColorChanged,
  dark,
}: EditWaypointColorProps): JSX.Element {
  const dispatch = useAppDispatch();

  const { projectApiClient } = useApiClientContext();
  const { handleErrorWithToast } = useErrorHandlers();

  const [isLoading, setIsLoading] = useState(false);

  const onAssignColor = useCallback(
    async (color: HoloBuilderColors | null) => {
      setIsLoading(true);

      try {
        await projectApiClient.applyMutations([
          createMutationSetWaypointColor(
            waypointReference.id,
            color ?? undefined,
          ),
        ]);

        await dispatch(
          updateProject({
            projectApi: projectApiClient,
            iElementQuery: {
              ids: [waypointReference.id],
            },
          }),
        );

        onColorChanged?.();
      } catch (error) {
        handleErrorWithToast({
          error,
          title: "Could not assign color to waypoint",
        });
      } finally {
        setIsLoading(false);
      }
    },
    [
      dispatch,
      handleErrorWithToast,
      onColorChanged,
      projectApiClient,
      waypointReference.id,
    ],
  );

  const { waypointsColoring } = useSettingsContext();

  return (
    <Stack aria-label="waypoint color" sx={{ px: 1.5, my: 1, mb: 2 }}>
      <Stack direction="row" sx={{ alignItems: "center", gap: 0.5 }}>
        <FaroText variant="labelS" textTransform="uppercase" dark={dark}>
          Color
        </FaroText>
        <WaypointColoringNotVisibleHint
          waypointsColoring={waypointsColoring}
          dark={dark}
        />
      </Stack>
      <LoadingOverlay isLoading={isLoading} dark={dark} size={20}>
        <SphereXGColorPalette
          hbColor={waypointReference.metaDataMap?.waypointColor}
          onHbColorChanged={onAssignColor}
          dark={dark}
        />
      </LoadingOverlay>
    </Stack>
  );
}

type WaypointColoringNotVisibleHintProps = {
  /** The current waypoint coloring options */
  waypointsColoring?: WaypointsColoringOptions;

  /** Whether to use dark styles */
  dark?: boolean;
};

function WaypointColoringNotVisibleHint({
  waypointsColoring,
  dark,
}: WaypointColoringNotVisibleHintProps): JSX.Element | null {
  if (waypointsColoring === WaypointsColoringOptions.custom) return null;

  const Tooltip = dark ? FaroTooltip : TooltipLight;
  const tooltipProps = dark ? {} : { sx: { boxShadow: 0 } };

  return (
    <Tooltip
      title={
        <FaroText variant="bodyM" dark={dark}>
          To see custom colors, enable the "{WaypointsColoringOptions.custom}"
          option in the General view settings.
        </FaroText>
      }
      {...tooltipProps}
    >
      <FaroIconButton size="xs" color={yellow[600]}>
        <WarningIcon />
      </FaroIconButton>
    </Tooltip>
  );
}

type SphereXGColorPaletteProps = {
  /** Optional label to show above the color palette */
  label?: string;

  /** Selected color */
  hbColor?: HoloBuilderColors | null;

  /** Callback when the color changes */
  onHbColorChanged(color: HoloBuilderColors | null): void;

  /** Whether to use dark styles */
  dark?: boolean;

  /** Styles to apply to the root container */
  sx?: SxProps;
};

/**
 * @returns A color palette to select a SphereXG color, but working with the mapped HoloBuilder colors as data.
 */
export function SphereXGColorPalette({
  label,
  hbColor,
  onHbColorChanged,
  dark,
  sx,
}: SphereXGColorPaletteProps): JSX.Element {
  const sphereXgColor = useMemo(
    () => (hbColor ? holoBuilderColorToSphereXGColor(hbColor) : null),
    [hbColor],
  );

  const onXgColorChanged = useCallback(
    (newSphereXGColor: string | null) => {
      if (newSphereXGColor === null) {
        onHbColorChanged(null);
        return;
      }

      assert(validateEnumValue(newSphereXGColor, SphereXGColors));
      onHbColorChanged(sphereXGToHolobuilderColors(newSphereXGColor));
    },
    [onHbColorChanged],
  );

  return (
    <Stack sx={sx}>
      {label && (
        <FaroText variant="labelM" dark={dark}>
          Color
        </FaroText>
      )}
      <ColorPalette
        colors={COLOR_OPTIONS}
        value={sphereXgColor}
        onChange={onXgColorChanged}
        dark={dark}
      />
    </Stack>
  );
}
